import { MouseEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button, Field, Input, Label, Switch } from "@headlessui/react";
import {
	CheckCircleIcon,
	LockClosedIcon,
	PhotoIcon,
	XCircleIcon,
} from "@heroicons/react/24/outline";
import { useAppSelector } from "../../../app/hooks";
import { isHigherThanDistrictAdmin, UserRole } from "../../../app/userSlice";
import ModalFrame from "../../../components/ModalFrame";
import { ClassLinkOrCleverIcon } from "../../../components/ClassLinkOrCleverIcon";
import RequiredField from "../../../components/RequiredField";
import Select from "../../../components/input/Select";
import { SimpleOption } from "../../../components/input/SimpleListbox";
import { useGetGradesQuery } from "../../lookup/lookupApiSlice";
import {
	AdminEditCourseEditModel,
	AdminEditCourseViewModel,
	useEditCourseMutation,
	useUpdateActiveSettingForCourseMutation,
	useUpdateUserAvatarSettingForCourseMutation,
} from "../coursesApiSlice";
import CourseOutsideSiteSettingsForm from "./CourseOutsideSiteSettingsForm";

interface Props {
	courseInfo: AdminEditCourseViewModel;
	addingNew: boolean;
	returnTo?: string;
	setErrorMessage: (message: string) => void;
	setCourseInfo: (courseInfo: AdminEditCourseViewModel) => void;
}

export default function CourseInformationSection(props: Props) {
	const { role } = useAppSelector((state) => state.user);
	const { config } = useAppSelector((state) => state.site);

	const [disableSave, setDisableSave] = useState(false);

	const [courseName, setCourseName] = useState(props.courseInfo.editModel.name ?? "");
	const [selectedGradeId, setSelectedGradeId] = useState(props.courseInfo.editModel.gradeId);
	const [externalId, setExternalId] = useState(props.courseInfo.editModel.externalID ?? "");
	const [outsideSiteId, setOutsideSiteId] = useState(
		props.courseInfo.showCleverId
			? (props.courseInfo.cleverID ?? "")
			: (props.courseInfo.classLinkID ?? "")
	);
	const [active, setActive] = useState(props.courseInfo.editModel.active ?? true);
	const [avatarsDisabled, setAvatarsDisabled] = useState(
		props.courseInfo.editModel.disableUserImages ?? true
	);

	const [gradeOptions, setGradeOptions] = useState<SimpleOption[]>([]);

	const [isOutsideSettingsFormOpen, setIsOutsideSettingsFormOpen] = useState(false);
	const showOutsideSite =
		!config?.isUtahComposeSite &&
		(props.courseInfo.showCleverId || props.courseInfo.showClassLinkId);

	const gradesResult = useGetGradesQuery();
	const grades = gradesResult.data;

	const [editCourse] = useEditCourseMutation();
	const [updateActiveSetting] = useUpdateActiveSettingForCourseMutation();
	const [updateAvatarSetting] = useUpdateUserAvatarSettingForCourseMutation();

	const navigate = useNavigate();

	useEffect(() => {
		if (!courseName) {
			setDisableSave(true);
		} else {
			setDisableSave(false);
		}
	}, [courseName]);

	useEffect(() => {
		const gradeOptions: SimpleOption[] =
			grades?.map((grade) => {
				return {
					value: grade.gradeId,
					label: grade.name,
				} as SimpleOption;
			}) ?? [];
		setGradeOptions(gradeOptions);
	}, [grades]);

	function updateEditModel() {
		const editModel: AdminEditCourseEditModel = {
			...props.courseInfo.editModel,
			name: courseName,
			gradeId: selectedGradeId,
			externalID: externalId,
			active: active,
			disableUserImages: avatarsDisabled,
		};
		return { id: props.courseInfo.id, editModel: editModel };
	}

	function handleCancel() {
		navigate(props.returnTo ?? `/schools/edit?schoolId=${props.courseInfo.schoolId}`);
	}

	function handleSave(e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) {
		e.preventDefault();
		const model = updateEditModel();

		if (model) {
			editCourse(model)
				.unwrap()
				.then(() => {
					navigate(
						props.returnTo ?? `/schools/edit?schoolId=${props.courseInfo.schoolId}`
					);
				})
				.catch((error) => {
					props.setErrorMessage(error.data);
					scrollTo(0, 0);
				});
		}
	}

	function handleActiveChange() {
		updateActiveSetting({ courseId: props.courseInfo.id, active: !active })
			.unwrap()
			.then(() => {
				setActive(!active);
				props.setErrorMessage("");
			})
			.catch((error) => {
				props.setErrorMessage(`Failed to save the active setting. ${error.data}`);
			});
	}

	function handleAvatarChange() {
		updateAvatarSetting({ courseId: props.courseInfo.id, disabled: !avatarsDisabled })
			.unwrap()
			.then(() => {
				setAvatarsDisabled(!avatarsDisabled);
				props.setErrorMessage("");
			})
			.catch((error) => {
				props.setErrorMessage(`Failed to save the user avatar setting. ${error.data}`);
			});
	}

	function handleOutsideSiteIdUpdate(id: string) {
		setOutsideSiteId(id);

		props.setCourseInfo({
			...props.courseInfo,
			cleverID: props.courseInfo.showCleverId ? id : props.courseInfo.cleverID,
			classLinkID: props.courseInfo.showClassLinkId ? id : props.courseInfo.classLinkID,
		});
	}

	const handleOutsideSettingsFormClose = () => setIsOutsideSettingsFormOpen(false);

	return (
		<section>
			<hr className="my-6" />
			<div className="w-full flex flex-col xl:flex-row divide-y xl:divide-x xl:divide-y-0 divide-gray-200 gap-y-4 gap-x-8">
				<div className="w-full xl:w-1/4 xl:pr-6">
					<h2 className="mb-2">Course Settings</h2>
					<div className="flex flex-row xl:flex-col gap-y-6 sm:gap-x-32 gap-x-24 ml-4 my-4 w-full">
						<div className="flex items-center">
							{active ? (
								<CheckCircleIcon className="size-18 text-green-500" />
							) : (
								<XCircleIcon className="size-18 text-red-500" />
							)}
							<div className="ml-4">
								<p
									className={`font-semibold ${active ? "text-green-700" : "text-red-700"}`}>
									{active ? "Active" : "Inactive"}
								</p>
								<Field className="flex items-center ml-2">
									<Label className="sr-only">Course Activation Toggle</Label>
									<Switch
										checked={active}
										onChange={handleActiveChange}
										name="activeToggle"
										className="group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
										<span
											aria-hidden="true"
											className="pointer-events-none inline-block size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
										/>
									</Switch>
								</Field>
							</div>
						</div>
						{!config?.isUtahComposeSite && (
							<div className="flex items-center">
								<PhotoIcon className="size-18" />
								<div className="ml-4">
									<p>Allow User Avatars:</p>
									<div className="mt-1">
										{props.courseInfo.editModel.disableUserImagesByDistrict ||
										props.courseInfo.editModel.disableUserImagesBySchool ? (
											<div className="flex items-center ml-2">
												<Switch
													checked={false}
													disabled={true}
													name="userAvatar"
													className="group relative inline-flex items-center h-7 w-12 shrink-0 rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden">
													<span
														aria-hidden="true"
														className="pointer-events-none absolute inset-0 flex items-center justify-center size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5">
														<LockClosedIcon className="size-5 items-center text-gray-500" />
													</span>
												</Switch>
												<p className="ml-2 text-sm">
													{props.courseInfo.editModel
														.disableUserImagesByDistrict
														? "Disabled on the district level."
														: "Disabled on the school level."}
												</p>
											</div>
										) : (
											<Field className="flex items-center ml-2">
												<Label className="sr-only">
													User Avatar Toggle
												</Label>
												<Switch
													checked={!avatarsDisabled}
													onChange={handleAvatarChange}
													name="userAvatar"
													className="group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
													<span
														aria-hidden="true"
														className="pointer-events-none inline-block size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
													/>
												</Switch>
											</Field>
										)}
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
				<div className="flex flex-col w-full xl:w-3/4">
					<div className="flex justify-between">
						<h2 className="mb-2">Course Details</h2>
						<p className="text-xs leading-6 text-gray-600">
							<RequiredField className="!text-sm" /> - Denotes a required field
						</p>
					</div>
					<div
						className={`${props.addingNew ? "gap-y-2" : "gap-y-3"} grid grid-cols-1 gap-x-6 sm:grid-cols-2`}>
						<Field className={`${props.addingNew ? "col-span-full" : "sm:col-span-1"}`}>
							<Label htmlFor="courseName" className="font-medium text-sm">
								Name
								<RequiredField />
							</Label>
							<Input
								id="courseName"
								name="courseName"
								type="text"
								required
								maxLength={125}
								className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
								value={courseName}
								onChange={(e) => setCourseName(e.target.value)}
							/>
						</Field>
						<Field className="sm:col-span-1">
							<Select
								value={selectedGradeId}
								setValue={(v) => setSelectedGradeId(v as number)}
								label="Grade"
								requiredField={true}
								simpleListBoxId="gradeDropdown"
								options={gradeOptions}
								fieldClasses="md:pt-0"
								simpleListBoxClasses="w-full"
							/>
						</Field>
						<Field className="col-span-full sm:col-span-1">
							<Label htmlFor="externalId" className="font-medium text-sm">
								External ID
							</Label>
							<Input
								id="externalId"
								name="externalId"
								type="text"
								maxLength={255}
								className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
								value={externalId}
								onChange={(e) => setExternalId(e.target.value)}
							/>
						</Field>
						<Field className="col-span-full sm:col-span-1">
							<Label htmlFor="referralCode" className="font-medium text-sm">
								Referral Code
							</Label>
							<div className="flex mt-1">
								<Input
									id="referralCode"
									name="referralCode"
									type="text"
									readOnly
									value={props.courseInfo.courseReferralCode ?? ""}
									className="rounded-lg block w-full border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
								/>
							</div>
						</Field>
					</div>
					<div className={`mt-3 mb-2 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3`}>
						{role === UserRole.Admin && showOutsideSite && (
							<Field className="col-span-full md:col-span-1">
								<Label htmlFor="classlinkClever" className="font-medium text-sm">
									{props.courseInfo.showCleverId ? "Clever" : "ClassLink"} ID
								</Label>
								<div className="flex mt-1">
									<Input
										id="classlinkClever"
										name="classlinkClever"
										type="text"
										readOnly
										className="block w-full rounded-none rounded-l-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={outsideSiteId}
									/>
									<Button
										id="outside-site-form-button"
										className="rounded-lg rounded-l-none border-0 ring-1 ring-inset ring-gray-900/20 cursor-pointer flex justify-center overflow-hidden"
										onClick={() => setIsOutsideSettingsFormOpen(true)}>
										<ClassLinkOrCleverIcon
											iconClasses="h-9 w-10 !rounded-lg !rounded-l-none"
											isClassLinkEntity={props.courseInfo.showClassLinkId}
											isCleverEntity={props.courseInfo.showCleverId}
										/>
									</Button>
								</div>
								{isOutsideSettingsFormOpen && (
									<ModalFrame
										open={isOutsideSettingsFormOpen}
										title={`${props.courseInfo.showCleverId ? "Clever Settings" : "ClassLink Settings"}`}
										onClose={handleOutsideSettingsFormClose}>
										<CourseOutsideSiteSettingsForm
											courseId={props.courseInfo.id}
											isCleverForm={props.courseInfo.showCleverId}
											onUpdateOutsideSiteId={handleOutsideSiteIdUpdate}
											onClose={handleOutsideSettingsFormClose}
										/>
									</ModalFrame>
								)}
							</Field>
						)}
						{role && isHigherThanDistrictAdmin(role) && (
							<div
								className={`${showOutsideSite ? "md:col-span-2" : ""} col-span-full mb-2 grid grid-cols-3 gap-x-6 gap-y-3`}>
								<Field className="col-span-full sm:col-span-1">
									<Label htmlFor="district" className="font-medium text-sm">
										District
									</Label>
									<Input
										id="district"
										name="district"
										type="text"
										readOnly
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={props.courseInfo.districtName ?? ""}
									/>
								</Field>
								<Field className="col-span-full sm:col-span-1">
									<Label htmlFor="school" className="font-medium text-sm">
										School
									</Label>
									<Input
										id="school"
										name="school"
										type="text"
										readOnly
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={props.courseInfo.schoolName}
									/>
								</Field>
								<Field className="col-span-full sm:col-span-1">
									<Label
										htmlFor="schoolExternalId"
										className="font-medium text-sm">
										School External ID
									</Label>
									<Input
										id="schoolExternalId"
										name="schoolExternalId"
										type="text"
										readOnly
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={props.courseInfo.schoolExternalId ?? ""}
									/>
								</Field>
							</div>
						)}
					</div>
					<div className="flex items-center justify-end gap-x-4">
						<Button type="button" className="btn btn-naked" onClick={handleCancel}>
							Cancel
						</Button>
						<Button
							type="submit"
							className="btn btn-primary"
							disabled={disableSave}
							onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleSave(e)}>
							Save
						</Button>
					</div>
				</div>
			</div>
		</section>
	);
}
